import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  FaReceipt,
  FaSearch,
  FaSpinner,
  FaEllipsisV,
  FaEye,
  FaUndoAlt,
  FaCalendarAlt,
  FaChartLine,
} from "react-icons/fa";

import apiRequest from "../services/api";
import InvoiceModal from "../components/InvoiceCreate";
import ReturnModal from "../components/ReturnCreate";
import InvoiceReceipt from "../components/InvoiceReceipt";
import PosShiftSummary from "../components/PosShiftSummary";

const SalesReport = () => {
  const [invoices, setInvoices] = useState([]);
  const [keyword, setKeyword] = useState("");
  const [salesPersonId, setSalesPersonId] = useState("");
  const [useCurrentUser, setUseCurrentUser] = useState(false);
  const [showCashReport, setShowCashReport] = useState(false);

  const today = new Date();
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(today.getDate() - 7);

  const formatDateString = (date) => date.toISOString().split("T")[0];
  const [startDate, setStartDate] = useState(formatDateString(sevenDaysAgo));
  const [endDate, setEndDate] = useState(formatDateString(today));

  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const size = 15;

  const [openReceipt, setOpenReceipt] = useState(false);
  const [activeInvoice, setActiveInvoice] = useState(null);
  const [receiptInvoice, setReceiptInvoice] = useState(null);
  const [totalSalesCount, setTotalSalesCount] = useState(0);
  const [totalSalesAmount, setTotalSalesAmount] = useState(0);

  const [modals, setModals] = useState({
    return: false,
    process: false,
  });

  const [activeMenuId, setActiveMenuId] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState({ top: 0, left: 0 });
  const menuContainerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        event.target.closest(".ellipsis-trigger-btn") ||
        (menuContainerRef.current &&
          menuContainerRef.current.contains(event.target))
      ) {
        return;
      }
      setActiveMenuId(null);
    };
    if (activeMenuId !== null) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [activeMenuId]);

  const observer = useRef();
  const lastElementRef = useCallback(
    (node) => {
      if (isLoading) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setPage((prev) => prev + 1);
        }
      });
      if (node) observer.current.observe(node);
    },
    [isLoading, hasMore]
  );

  const fetchSalesReport = useCallback(
    async (targetPage, isNewSearch = false) => {
      setIsLoading(true);
      try {
        const queryParams = new URLSearchParams({
          keyword: keyword.trim(),
          mySales: useCurrentUser,
          type: "SALE",
          startDate: `${startDate}T00:00:00`,
          endDate: `${endDate}T23:59:59`,
          page: targetPage,
          size: size,
        });

        const response = await apiRequest(
          `invoices/sales-report?${queryParams.toString()}`
        );
        const content = response?.data?.invoices?.content || [];
        const last = response?.data?.last ?? true;
        setTotalSalesCount(response?.data?.totalSalesCount);
        setTotalSalesAmount(response?.data?.totalSalesValue);
        setInvoices((prev) => (isNewSearch ? content : [...prev, ...content]));
        setHasMore(!last && content.length === size);
      } catch (err) {
        console.error("Failed to fetch sales report database records:", err);
        if (isNewSearch) setInvoices([]);
      } finally {
        setIsLoading(false);
      }
    },
    [keyword, startDate, endDate, useCurrentUser, salesPersonId, size]
  );

  useEffect(() => {
    setPage(0);
    fetchSalesReport(0, true);
  }, [
    keyword,
    startDate,
    endDate,
    salesPersonId,
    useCurrentUser,
    fetchSalesReport,
  ]);

  useEffect(() => {
    if (page > 0) fetchSalesReport(page, false);
  }, [page, fetchSalesReport]);

  const handleOpenActionModal = (invoice, modalType) => {
    setActiveInvoice(invoice);
    setActiveMenuId(null);
    setModals((prev) => ({ ...prev, [modalType]: true }));
  };

  const getStatusBadgeStyles = (status) => {
    switch (status?.toUpperCase()) {
      case "PAID":
      case "DELIVERED":
      case "RECEIVED":
        return "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-400 dark:border-emerald-500/20";
      case "PARTIALLY_PAID":
        return "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-500/10 dark:text-blue-400 dark:border-blue-500/20";
      case "CONFIRMED":
        return "bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-500/10 dark:text-purple-400 dark:border-purple-500/20";
      default:
        return "bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-500/10 dark:text-slate-400 dark:border-white/5";
    }
  };

  const formatCurrency = (value) => {
    return `ETB ${Number(value || 0).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const menuButtonClass =
    "w-full text-left px-3 py-2 rounded-lg text-xs transition flex items-center gap-2 font-medium text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-white/10";

  const inputCls =
    "w-full rounded-xl pl-9 pr-4 py-2 text-xs focus:outline-none transition h-9 bg-white border border-slate-200 text-slate-800 placeholder-slate-400 focus:border-blue-500/40 dark:bg-slate-950/60 dark:border-white/10 dark:text-white dark:placeholder-slate-600";

  return (
    <div
      className="min-h-full p-4 md:p-8 font-sans
      bg-slate-50 text-slate-800 selection:bg-amber-500/20 selection:text-slate-900
      dark:bg-slate-950 dark:text-slate-100 dark:selection:bg-amber-500/30 dark:selection:text-white"
    >
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div
          className="flex items-center gap-3.5 p-5 rounded-2xl shadow-sm border backdrop-blur-md
          bg-white border-slate-200
          dark:bg-slate-900/40 dark:border-white/10 dark:shadow-xl"
        >
          <div
            className="p-3 rounded-xl shadow-inner border
            bg-gradient-to-br from-blue-100 to-indigo-50 border-blue-200 text-blue-600
            dark:from-blue-500/20 dark:to-indigo-500/10 dark:border-blue-500/30 dark:text-blue-400"
          >
            <FaChartLine className="text-2xl" />
          </div>
          <div>
            <h1 className="text-xl font-black tracking-tight uppercase text-slate-900 dark:text-white">
              Sales Report
            </h1>
            <p className="text-xs mt-0.5 text-slate-500 dark:text-slate-400">
              Excluding all draft & cancelled invoices
            </p>
          </div>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div
            className="p-5 rounded-2xl border shadow-sm backdrop-blur-xl
            bg-white border-slate-200
            dark:bg-white/[0.02] dark:border-white/[0.08] dark:shadow-lg"
          >
            <span className="text-xs uppercase font-semibold tracking-wider text-slate-500 dark:text-slate-400">
              # Count
            </span>
            <h2 className="text-3xl font-black mt-1 text-blue-600 dark:text-blue-400">
              {totalSalesCount}{" "}
              <span className="text-sm font-normal text-slate-500">Sales</span>
            </h2>
          </div>
          <div
            className="p-5 rounded-2xl border shadow-sm backdrop-blur-xl
            bg-white border-slate-200
            dark:bg-white/[0.02] dark:border-white/[0.08] dark:shadow-lg"
          >
            <span className="text-xs uppercase font-semibold tracking-wider text-slate-500 dark:text-slate-400">
              Sales Value
            </span>
            <h2 className="text-3xl font-black mt-1 text-emerald-600 dark:text-emerald-400">
              {formatCurrency(totalSalesAmount)}
            </h2>
          </div>
          <div
            className="p-5 rounded-2xl border shadow-sm backdrop-blur-xl flex items-center
            bg-white border-slate-200
            dark:bg-white/[0.02] dark:border-white/[0.08] dark:shadow-lg"
          >
            <button
              onClick={() => setShowCashReport(true)}
              className="px-4 py-2 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-xl text-white text-xs font-bold hover:shadow-lg transition-all"
            >
              View Daily Cash Report
            </button>
          </div>
        </div>

        {/* Filters */}
        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 p-4 rounded-xl border backdrop-blur-sm
          bg-white border-slate-200
          dark:bg-slate-900/20 dark:border-white/5"
        >
          <div className="relative">
            <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400 dark:text-slate-500">
              <FaSearch className="text-xs" />
            </span>
            <input
              type="text"
              placeholder="Search reference or partner..."
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              className={inputCls}
            />
          </div>

          <div
            className="flex items-center gap-3 rounded-xl px-4 h-9 select-none border
            bg-slate-50 border-slate-200
            dark:bg-slate-950/40 dark:border-white/10"
          >
            <label className="relative flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={useCurrentUser}
                onChange={(e) => setUseCurrentUser(e.target.checked)}
                className="sr-only peer"
              />
              <div
                className="w-4 h-4 rounded flex items-center justify-center transition peer-focus:ring-2 peer-focus:ring-blue-500/40
                bg-white border border-slate-300 peer-checked:bg-blue-600 peer-checked:border-blue-500
                dark:bg-slate-900 dark:border-white/20"
              >
                {useCurrentUser && (
                  <svg
                    className="w-2.5 h-2.5 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth="4"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                )}
              </div>
              <span className="ml-2.5 text-xs font-medium transition text-slate-600 peer-checked:text-slate-900 dark:text-slate-300 dark:peer-checked:text-white">
                My Sales Only
              </span>
            </label>
          </div>

          <div className="relative">
            <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400 dark:text-slate-500">
              <FaCalendarAlt className="text-xs" />
            </span>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className={inputCls}
            />
          </div>

          <div className="relative">
            <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400 dark:text-slate-500">
              <FaCalendarAlt className="text-xs" />
            </span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className={inputCls}
            />
          </div>
        </div>

        {/* Table */}
        <div
          className="border rounded-2xl shadow-sm backdrop-blur-md
          bg-white border-slate-200
          dark:bg-slate-900/30 dark:border-white/10 dark:shadow-2xl"
        >
          <div className="overflow-x-auto rounded-2xl">
            <table className="w-full text-left border-collapse min-w-[900px]">
              <thead>
                <tr
                  className="border-b text-[10px] font-bold tracking-wider uppercase
                  bg-slate-50 border-slate-200 text-slate-500
                  dark:bg-slate-950/50 dark:border-white/10 dark:text-slate-400"
                >
                  <th className="px-5 py-4">Reference</th>
                  <th className="px-4 py-4">Customer</th>
                  <th className="px-4 py-4">Status</th>
                  <th className="px-4 py-4 text-right">Total Amount</th>
                  <th className="px-4 py-4 text-right">Paid Value</th>
                  <th className="px-4 py-4 text-right">Balance Due</th>
                  <th className="px-5 py-4 text-center w-24">Actions</th>
                </tr>
              </thead>
              <tbody
                className="divide-y font-mono text-xs
                divide-slate-100 text-slate-600
                dark:divide-white/5 dark:text-slate-300"
              >
                {invoices.map((invoice, index) => {
                  const isLastRow = invoices.length === index + 1;
                  return (
                    <tr
                      key={invoice.id}
                      ref={isLastRow ? lastElementRef : null}
                      className="group transition relative hover:bg-slate-50 dark:hover:bg-white/[0.02]"
                    >
                      <td className="px-5 py-3.5 font-sans font-bold tracking-wide text-slate-900 dark:text-white">
                        {invoice.invoiceNumber}
                      </td>
                      <td className="px-4 py-3.5 font-sans text-slate-500 dark:text-slate-400">
                        {invoice.partnerName || "N/A"}
                      </td>
                      <td className="px-4 py-3.5 font-sans">
                        <span
                          className={`px-2 py-0.5 rounded text-[10px] font-bold tracking-wider uppercase border ${getStatusBadgeStyles(
                            invoice.status
                          )}`}
                        >
                          {invoice.status}
                        </span>
                      </td>
                      <td className="px-4 py-3.5 text-right font-semibold text-slate-700 dark:text-slate-200">
                        {formatCurrency(invoice.totalAmount)}
                      </td>
                      <td className="px-4 py-3.5 text-right font-medium text-emerald-600 dark:text-emerald-400">
                        {formatCurrency(invoice.paidAmount)}
                      </td>
                      <td className="px-4 py-3.5 text-right font-medium text-amber-600 dark:text-amber-500">
                        {formatCurrency(invoice.balanceAmount)}
                      </td>
                      <td className="px-5 py-3.5 text-center font-sans">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (activeMenuId === invoice.id) {
                              setActiveMenuId(null);
                            } else {
                              const rect =
                                e.currentTarget.getBoundingClientRect();
                              setMenuAnchor({
                                top: rect.bottom + window.scrollY + 6,
                                left: rect.left + window.scrollX - 156,
                              });
                              setActiveInvoice(invoice);
                              setActiveMenuId(invoice.id);
                            }
                          }}
                          className="ellipsis-trigger-btn p-2 rounded-xl border transition
                            bg-slate-50 hover:bg-slate-100 border-slate-200 text-slate-500 hover:text-slate-800
                            dark:bg-white/5 dark:hover:bg-white/10 dark:border-white/5 dark:text-slate-400 dark:hover:text-white"
                        >
                          <FaEllipsisV className="text-xs pointer-events-none" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {isLoading && (
            <div
              className="p-8 flex items-center justify-center gap-3 text-xs border-t font-sans
              bg-slate-50 text-slate-500 border-slate-200
              dark:bg-slate-950/20 dark:text-slate-400 dark:border-white/5"
            >
              <FaSpinner className="animate-spin text-blue-500 text-base" />
              <span>Compiling loaded metrics pipeline updates...</span>
            </div>
          )}

          {!hasMore && invoices.length > 0 && (
            <div
              className="p-4 text-center text-[11px] border-t font-sans italic
              text-slate-400 bg-slate-50 border-slate-200
              dark:text-slate-600 dark:bg-slate-950/40 dark:border-white/5"
            >
              All timeframe report records synchronized.
            </div>
          )}
        </div>
      </div>

      {/* Context menu */}
      {activeMenuId !== null && (
        <div
          ref={menuContainerRef}
          className="fixed w-52 rounded-xl shadow-2xl p-1.5 z-[999] text-left backdrop-blur-xl space-y-0.5 border
            bg-white border-slate-200
            dark:bg-slate-900 dark:border-white/10 dark:bg-opacity-95"
          style={{ top: menuAnchor.top, left: menuAnchor.left }}
        >
          {activeInvoice?.status?.toUpperCase() === "PAID" && (
            <button
              onClick={() => {
                setReceiptInvoice(activeInvoice);
                setOpenReceipt(true);
                setActiveMenuId(null);
              }}
              className={menuButtonClass}
            >
              <FaReceipt className="text-slate-400 text-sm" />
              Open Receipt
            </button>
          )}
          <button
            type="button"
            onClick={() => handleOpenActionModal(activeInvoice, "process")}
            className={menuButtonClass}
          >
            <FaEye className="text-slate-400 text-sm" />
            View Record
          </button>
          <button
            type="button"
            onClick={() => handleOpenActionModal(activeInvoice, "return")}
            className={menuButtonClass}
          >
            <FaUndoAlt className="text-amber-500 text-sm" />
            Initiate Return Process
          </button>
        </div>
      )}

      {modals.process && (
        <InvoiceModal
          isOpen={modals.process}
          invoice={activeInvoice}
          initialMode="EDIT"
          invoiceType="SALE"
          onClose={() => setModals((prev) => ({ ...prev, process: false }))}
          onSaveSuccess={() => fetchSalesReport(0, true)}
        />
      )}

      {modals.return && (
        <ReturnModal
          isOpen={modals.return}
          invoice={activeInvoice}
          onClose={() => setModals((prev) => ({ ...prev, return: false }))}
          onSaveSuccess={() => fetchSalesReport(0, true)}
        />
      )}

      <InvoiceReceipt
        invoiceId={receiptInvoice?.id}
        isOpen={openReceipt}
        onClose={() => {
          setOpenReceipt(false);
          setReceiptInvoice(null);
          type = "SALE";
        }}
      />
      <PosShiftSummary
        isOpen={showCashReport}
        onClose={() => setShowCashReport(false)}
      />
    </div>
  );
};

export default SalesReport;
